import {
  endOfWeek,
  getISOWeek,
  getISOWeekYear,
  setISOWeek,
  startOfWeek,
} from 'date-fns';
import { useCallback, useMemo } from 'react';
import type { DateRange } from 'react-day-picker';
import { usePurchasePlanningWeekParams } from '@/features/purchase-planning/hooks/use-purchase-planning-week-params';

const weekOptions = { weekStartsOn: 1 as const };

export function useWeekRangeCalendar() {
  const { weekParams, setWeekParams } = usePurchasePlanningWeekParams();

  const dateRange: DateRange | undefined = useMemo(() => {
    if (!(weekParams?.year && weekParams?.week)) {
      return;
    }

    const startBaseDate = new Date(weekParams.year, 0, 4);
    const startWeekDate = setISOWeek(startBaseDate, weekParams.week);
    const from = startOfWeek(startWeekDate, weekOptions);

    const endBaseDate = new Date(weekParams.endYear ?? weekParams.year, 0, 4);
    const endWeekDate = setISOWeek(
      endBaseDate,
      weekParams.endWeek ?? weekParams.week,
    );
    const to = endOfWeek(endWeekDate, weekOptions);

    return { from, to };
  }, [
    weekParams.year,
    weekParams.week,
    weekParams.endYear,
    weekParams.endWeek,
  ]);

  const reset = useCallback(() => {
    setWeekParams({
      year: null,
      week: null,
      endYear: null,
      endWeek: null,
      page: 1,
    });
  }, [setWeekParams]);

  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from) {
        reset();
        return;
      }

      const startWeekStart = startOfWeek(range.from, weekOptions);
      const endRefDate = range.to ?? range.from;
      const endWeekStart = startOfWeek(endRefDate, weekOptions);

      const year = getISOWeekYear(startWeekStart);
      const week = getISOWeek(startWeekStart);
      const endYear = getISOWeekYear(endWeekStart);
      const endWeek = getISOWeek(endWeekStart);

      setWeekParams({ year, week, endYear, endWeek, page: 1 });
    },
    [setWeekParams, reset],
  );

  return {
    dateRange,
    handleSelect,
    reset,
  };
}
